import { createContext, forwardRef, PropsWithChildren, ReactNode, useCallback, useMemo, useRef, useState } from 'react';
import { OptionalLink } from '../../optional-link/optional-link.component';
import { DeliveryContainerTileStyleProps, useDeliveryContainerTileStyles } from './delivery-container-tile.styles';
import { Tooltip } from '../../tooltip/tooltip';

type DeliveryContainerTileTooltipContextValue = {
  suppressTooltip(): void;
  unsuppressTooltip(): void;
};

export const DeliveryContainerTileTooltipContext = createContext<DeliveryContainerTileTooltipContextValue>({
  suppressTooltip: () => {},
  unsuppressTooltip: () => {}
});

export type DeliveryContainerTileProps = DeliveryContainerTileStyleProps & {
  link?: string;
  linkRequiresPermissions?: boolean;
  hideTooltip?: boolean;
  disablePointer?: boolean;
  tooltipContent?: ReactNode;
};

export function DeliveryContainerTile({
  pixelDimensions2D,
  pixelPosition2D,
  backgroundColor,
  isHighlighted,
  highlightColor,
  disablePointer,
  children,
  hideTooltip,
  tooltipContent,
  link,
  linkRequiresPermissions
}: PropsWithChildren<DeliveryContainerTileProps>) {
  const classes = useDeliveryContainerTileStyles({
    pixelDimensions2D,
    pixelPosition2D,
    backgroundColor,
    isHighlighted,
    highlightColor,
    disablePointer
  });
  const [isTooltipOpen, setTooltipOpen] = useState(false);
  const tooltipSuppressedRef = useRef(false);

  const suppressTooltip = useCallback(() => {
    tooltipSuppressedRef.current = true;
    setTooltipOpen(false);
  }, []);

  const unsuppressTooltip = useCallback(() => {
    tooltipSuppressedRef.current = false;
  }, []);

  const tooltipContextValue = useMemo(
    () => ({ suppressTooltip, unsuppressTooltip }),
    [suppressTooltip, unsuppressTooltip]
  );

  const availableUiFeatures = linkRequiresPermissions ? ['internal-groundfloor'] : undefined;

  return (
    <DeliveryContainerTileTooltipContext.Provider value={tooltipContextValue}>
      <OptionalLink link={link} availableUiFeatures={availableUiFeatures}>
        <div className={classes.root}>
          {tooltipContent ? (
            <Tooltip
              classes={{ tooltip: classes.tooltip }}
              width="495px"
              title={tooltipContent}
              open={isTooltipOpen && !hideTooltip}
              onOpen={() => {
                if (!tooltipSuppressedRef.current && !hideTooltip) {
                  setTooltipOpen(true);
                }
              }}
              onClose={() => setTooltipOpen(false)}
              disableTouchListener
              disableFocusListener
              placement="right-start"
              slotProps={{
                transition: {
                  timeout: {
                    appear: 750,
                    enter: 750,
                    exit: 0
                  }
                }
              }}
              enterDelay={300}
              enterTouchDelay={300}
              enterNextDelay={300}
              leaveDelay={300}
              leaveTouchDelay={300}
            >
              <ChildrenWithRef>{children}</ChildrenWithRef>
            </Tooltip>
          ) : (
            <>{children}</>
          )}
        </div>
      </OptionalLink>
    </DeliveryContainerTileTooltipContext.Provider>
  );
}

// The MUI tooltip requires that its ref and child props (it adds props to its children) are passed down to the native element being rendered
const ChildrenWithRef = forwardRef<HTMLDivElement, PropsWithChildren<{}>>(function ChildrenWithRef(
  { children, ...rest },
  ref
) {
  return (
    <div ref={ref} {...rest} style={{ height: '100%', width: '100%' }}>
      {children}
    </div>
  );
});
